<template>
  <div id="ldmt-consolidate-report">
    <overlay-loading-progress :LoadingFlag="OverlayLoadingFlag" />
    <!-- Premium Header Banner -->
    <v-card class="premium-header-card pa-8 mb-10 overflow-hidden" dark flat>
      <!-- Animated Background Bubbles -->
      <div class="header-bubbles">
        <div class="bubble b-1"></div>
        <div class="bubble b-2"></div>
        <div class="bubble b-3"></div>
        <div class="bubble b-4"></div>
        <div class="bubble b-5"></div>
      </div>

      <v-row align="center" style="position: relative; z-index: 2;">
        <v-col cols="12" md="8">
          <div class="d-flex align-center mb-2">
            <v-icon large class="mr-3 gold--text">mdi-seal-variant</v-icon>
            <span class="text-overline font-weight-bold gold--text" style="letter-spacing: 3px !important">
              {{ YearName }} OFFICIAL REPORT
            </span>
          </div>
          <h1 class="text-h3 font-weight-black mb-2 white--text">
            {{ PageTitle }}
          </h1>
          <p class="text-h6 font-weight-medium white--text opacity-80">
            {{ PageDescription }}
          </p>
        </v-col>
        <v-col cols="12" md="4" class="text-right align-self-center">
          <v-btn fab color="rgba(255,255,255,0.2)" class="elevation-8" @click="searchForm" :loading="LoadingFlag"
            x-large>
            <v-icon color="white" size="40">mdi-refresh</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Glassmorphism Search Card -->
    <v-container>
      <v-card class="search-card pa-6 mb-8">
        <v-form ref="form2" v-model="valid1" lazy-validation id="createForm">
          <v-row align="center">
            <v-col cols="12" md="3">
              <div class="d-flex align-center mb-1">
                <v-icon small color="primary" class="mr-2">mdi-map-marker-radius</v-icon>
                <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2">Select Area</span>
              </div>
              <v-autocomplete @change="getZoneCodeOptions" :reduce="(option) => option.value"
                :loading="AreaCodeOptionsLoading" :items="AreaCodeOptions" :rules="AreaCodeRules" v-model="AreaCode"
                dense clearable required outlined rounded placeholder="Choose Area"></v-autocomplete>
            </v-col>
            <v-col cols="12" md="3">
              <div class="d-flex align-center mb-1">
                <v-icon small color="primary" class="mr-2">mdi-rhombus-split</v-icon>
                <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2">Select Zone</span>
              </div>
              <v-autocomplete @change="getRegionCodeOptions" :reduce="(option) => option.value"
                :loading="ZoneCodeOptionsLoading" :items="ZoneCodeOptions" :rules="ZoneCodeRules" v-model="ZoneCode"
                dense clearable required outlined rounded placeholder="Choose Zone"></v-autocomplete>
            </v-col>
            <v-col cols="12" md="3">
              <div class="d-flex align-center mb-1">
                <v-icon small color="primary" class="mr-2">mdi-compass-outline</v-icon>
                <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2">Select Region</span>
              </div>
              <v-autocomplete :reduce="(option) => option.value" :loading="RegionCodeOptionsLoading"
                :items="RegionCodeOptions" :rules="RegionCodeRules" v-model="RegionCode" dense clearable required
                outlined rounded placeholder="Choose Region"></v-autocomplete>
            </v-col>
            <v-col cols="12" md="3" class="d-flex align-center pt-6">
              <v-btn :disabled="!valid1" @click.prevent="searchForm()" :loading="SearchFlag" dark
                class="premium-btn btn-search flex-grow-1 mr-2" large>
                <v-icon left>mdi-magnify</v-icon> Search
              </v-btn>
              <v-btn @click.prevent="resetForm" dark class="premium-btn btn-reset" icon large>
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-container>
    <v-container class="py-0" v-if="StartupLoadingFlag">
      <v-skeleton-loader v-bind="attrs" type="table-tbody"></v-skeleton-loader>
    </v-container>
    <v-container class="py-0" v-if="tableData1 <= 0">
      <v-row wrap v-if="tableData2.length == 0">
        <v-col align="center" cols="12" md="12">
          <h4>No records found.</h4>
        </v-col>
      </v-row>
    </v-container>
    <v-container class="py-0" v-if="ResultFlag && tableData1.length > 0">
      <v-row justify="center" class="mb-10">
        <v-col cols="12" md="8" class="text-center">
          <div class="excel-action-box pa-6 rounded-xl elevation-2 bg-white border">
            <v-btn v-if="!ExcelDownloadFlag" @click.prevent="generateExcelReport" :loading="ExcelLoadingFlag"
              class="premium-btn btn-excel-generate px-10 py-6 mb-4" x-large dark>
              <v-icon left size="28">mdi-file-excel-box</v-icon>
              Generate Detailed Report
            </v-btn>
            <v-btn v-if="ExcelFileName != '' && ExcelDownloadFlag == 1" :href="ExcelFileName" target="_blank"
              class="premium-btn btn-excel-download px-10 py-6 mb-4" x-large dark>
              <v-icon left size="28">mdi-cloud-download</v-icon>
              Download Report Now
            </v-btn>

            <div class="d-flex align-center justify-center mt-2 opacity-80">
              <v-icon small color="amber darken-3" class="mr-2">mdi-information-outline</v-icon>
              <span class="text-caption font-weight-bold grey--text text--darken-2">
                Note: Generating the consolidated analysis may take a few moments. Please remain on this page until the
                download is ready.
              </span>
            </div>
          </div>
        </v-col>
      </v-row>
      <!-- Premium Statistic Row -->
      <v-row class="mb-8 px-4">
        <v-col cols="12" md="4">
          <v-card class="stat-card zvp-premium-card pa-4 h-100" dark>
            <div class="d-flex justify-space-between align-start">
              <div>
                <div class="text-overline mb-1 opacity-70">Zonal Vice President</div>
                <div class="text-h5 font-weight-bold mb-1">{{ tableColumns1.ZvpName }}</div>
                <div class="text-caption mb-3 d-flex align-center">
                  <v-icon x-small class="mr-1">mdi-account-circle</v-icon>
                  {{ tableColumns1.MemberId }}
                </div>
              </div>
              <v-avatar size="64" class="elevation-4 grey lighten-4">
                <v-img v-if="tableColumns1.MemberPhoto != ''" :src="tableColumns1.MemberPhoto" />
                <v-icon v-else color="primary" large>mdi-account</v-icon>
              </v-avatar>
            </div>
            <v-divider class="my-3 opacity-20"></v-divider>
            <div class="d-flex align-center mb-1">
              <v-icon small class="mr-2">mdi-bank</v-icon>
              <span class="text-body-2">{{ tableColumns1.Zone }}</span>
            </div>
            <div class="d-flex align-center mb-1">
              <v-icon small class="mr-2">mdi-office-building</v-icon>
              <span class="text-body-2">{{ tableColumns1.LomName }}</span>
            </div>
            <div class="d-flex align-center">
              <v-icon small class="mr-2">mdi-phone-in-talk</v-icon>
              <span class="text-body-2">{{ tableColumns1.ZvpMobileNumber }}</span>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="stat-card ldmt-premium-card pa-4 h-100" dark>
            <div class="d-flex justify-space-between align-center mb-4">
              <div class="text-overline opacity-70">First Half Statistics</div>
              <v-icon large>mdi-chart-donut</v-icon>
            </div>
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="text-body-2">Completion Rate</span>
              <span class="text-h6 font-weight-black">{{ Records.LdmtPercentage }}%</span>
            </div>
            <v-progress-linear :value="Records.LdmtPercentage" color="white" height="10" rounded
              class="mb-6 elevation-1"></v-progress-linear>
            <v-row no-gutters>
              <v-col cols="4" class="text-center">
                <div class="text-h6 font-weight-bold">{{ LdmtData.PendingCount }}</div>
                <div class="text-caption opacity-70">Pending</div>
              </v-col>
              <v-col cols="4" class="text-center">
                <div class="text-h6 font-weight-bold">{{ LdmtData.CompletedCount }}</div>
                <div class="text-caption opacity-70">Reported</div>
              </v-col>
              <v-col cols="4" class="text-center">
                <div class="text-h6 font-weight-bold">{{ LdmtData.ApprovalCount }}</div>
                <div class="text-caption opacity-70">Approved</div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="stat-card lav-premium-card pa-4 h-100" dark>
            <div class="d-flex justify-space-between align-center mb-4">
              <div class="text-overline opacity-70">Second Half Statistics</div>
              <v-icon large>mdi-chart-line-variant</v-icon>
            </div>
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="text-body-2">Completion Rate</span>
              <span class="text-h6 font-weight-black">{{ Records.LavPercentage }}%</span>
            </div>
            <v-progress-linear :value="Records.LavPercentage" color="white" height="10" rounded
              class="mb-6 elevation-1"></v-progress-linear>
            <v-row no-gutters>
              <v-col cols="4" class="text-center">
                <div class="text-h6 font-weight-bold">{{ LavData.PendingCount }}</div>
                <div class="text-caption opacity-70">Pending</div>
              </v-col>
              <v-col cols="4" class="text-center">
                <div class="text-h6 font-weight-bold">{{ LavData.CompletedCount }}</div>
                <div class="text-caption opacity-70">Reported</div>
              </v-col>
              <v-col cols="4" class="text-center">
                <div class="text-h6 font-weight-bold">{{ LavData.ApprovalCount }}</div>
                <div class="text-caption opacity-70">Approved</div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <!-- Premium Training Records -->
      <v-container v-if="ResultFlag && tableData1.length > 0">
        <v-row v-for="(Details, index) in tableData1" :key="index" class="mb-10">
          <v-col cols="12">
            <v-card class="lom-details-card pa-6 overflow-hidden" elevation="2">
              <v-row>
                <!-- LO Info Section -->
                <v-col cols="12" md="4" class="pr-md-8 border-right-md">
                  <div class="d-flex align-center mb-6">
                    <v-avatar color="indigo darken-4" size="48" class="mr-4 elevation-2">
                      <v-icon color="white">mdi-office-building</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-overline primary--text font-weight-bold" style="line-height: 1">LOM Information
                      </div>
                      <div class="text-h6 font-weight-bold">{{ Details.LomName }}</div>
                    </div>
                  </div>

                  <v-list dense class="pa-0 transparent">
                    <v-list-item class="px-0 min-h-0 mb-3">
                      <v-list-item-avatar size="32" class="my-0 mr-3 grey lighten-3">
                        <v-icon small color="indigo">mdi-account-tie</v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content class="py-0">
                        <v-list-item-subtitle class="text-caption">President</v-list-item-subtitle>
                        <v-list-item-title class="text-body-2 font-weight-bold">{{ Details.PresidentName }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item class="px-0 min-h-0">
                      <v-list-item-avatar size="32" class="my-0 mr-3 grey lighten-3">
                        <v-icon small color="indigo">mdi-phone</v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content class="py-0">
                        <v-list-item-subtitle class="text-caption">Contact Number</v-list-item-subtitle>
                        <v-list-item-title class="text-body-2 font-weight-bold">{{ Details.PresidentMobileNo }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>

                <!-- First Half / LDMT Section -->
                <v-col cols="12" md="4" class="px-md-8 border-right-md">
                  <v-card flat class="training-category-card ldmt-box pa-4">
                    <div class="d-flex align-center justify-space-between mb-4">
                      <span class="text-subtitle-1 font-weight-black green--text text--darken-3">FIRST HALF</span>
                      <v-chip small :color="Details.CompletedStatusColor" class="custom-chip white--text elevation-1">
                        {{ Details.CompletedStatusTxt }}
                      </v-chip>
                    </div>

                    <div class="mb-4">
                      <div class="d-flex justify-space-between mb-1">
                        <span class="text-caption grey--text text--darken-1">President Date</span>
                        <span class="text-caption font-weight-bold">{{ Details.LdmtDateTxt }}</span>
                      </div>
                      <div class="d-flex justify-space-between mb-1">
                        <span class="text-caption grey--text text--darken-1">ZVP Date</span>
                        <span class="text-caption font-weight-bold">{{ Details.ZvpLdmtDate || 'Pending' }}</span>
                      </div>
                      <div class="d-flex justify-space-between align-center mb-1">
                        <span class="text-caption grey--text text--darken-1">NHQ Acknowledgement</span>
                        <v-chip x-small :color="Details.LdmtNhqApprovalStatusColor" dark class="font-weight-bold">
                          {{ Details.LdmtNhqApprovalStatusTxt }}
                        </v-chip>
                      </div>
                      <div class="d-flex justify-space-between align-center">
                        <span class="text-caption grey--text text--darken-1">GLC Recommendation</span>
                        <v-chip x-small :color="getGlcStatusColor(Details.LdmtGLCStatus)" dark class="font-weight-bold">
                          {{ getGlcStatusTxt(Details.LdmtGLCStatus) }}
                        </v-chip>
                      </div>
                    </div>

                    <div class="d-flex flex-wrap gap-2 pt-2 border-top">
                      <v-btn v-if="Details.DownloadFlag" :href="Details.FilePath" target="_blank" color="error" x-small
                        class="premium-btn elevation-2 mr-2 mb-2">
                        <v-icon left x-small>mdi-file-pdf-box</v-icon> PDF
                      </v-btn>
                      <v-btn v-if="Details.LdmtRegenerateFlag" @click="regeneratePDF(Details.LomTrainingId)"
                        color="amber darken-2" dark x-small class="premium-btn elevation-2 mb-2">
                        <v-icon left x-small>mdi-sync</v-icon> Re-Generate
                      </v-btn>
                      <v-btn v-if="Details.ApprovalFlag" color="success" x-small
                        class="premium-btn elevation-2 mr-2 mb-2" @click="ApprovalStatus(Details)">
                        <v-icon left x-small>mdi-check-decagram</v-icon> Acknowledgement
                      </v-btn>
                      <v-btn v-if="Details.LdmtGLCApprovalFlag" color="indigo" dark x-small
                        class="premium-btn elevation-2 mr-2 mb-2" @click="GlcAcknowledgement(Details, 1)">
                        <v-icon left x-small>mdi-shield-check</v-icon> GLC Recommendation
                      </v-btn>

                    </div>
                  </v-card>
                </v-col>

                <!-- Second Half / LAV Section -->
                <v-col cols="12" md="4" class="pl-md-8">
                  <v-card flat class="training-category-card lav-box pa-4">
                    <div class="d-flex align-center justify-space-between mb-4">
                      <span class="text-subtitle-1 font-weight-black light-blue--text text--darken-3">SECOND HALF</span>
                      <v-chip small :color="Details.LavCompletedStatusColor"
                        class="custom-chip white--text elevation-1">
                        {{ Details.LavCompletedStatusTxt }}
                      </v-chip>
                    </div>

                    <div class="mb-4">
                      <div class="d-flex justify-space-between mb-1">
                        <span class="text-caption grey--text text--darken-1">President Date</span>
                        <span class="text-caption font-weight-bold">{{ Details.LavDateTxt }}</span>
                      </div>
                      <div class="d-flex justify-space-between mb-1">
                        <span class="text-caption grey--text text--darken-1">ZVP Date</span>
                        <span class="text-caption font-weight-bold">{{ Details.ZvpLavDate || 'Pending' }}</span>
                      </div>
                      <div class="d-flex justify-space-between align-center mb-1">
                        <span class="text-caption grey--text text--darken-1">NHQ Acknowledgement</span>
                        <v-chip x-small :color="Details.LavNhqApprovalStatusColor" dark class="font-weight-bold">
                          {{ Details.LavNhqApprovalStatusTxt }}
                        </v-chip>
                      </div>
                      <div class="d-flex justify-space-between align-center">
                        <span class="text-caption grey--text text--darken-1">GLC Recommendation</span>
                        <v-chip x-small :color="getGlcStatusColor(Details.LavGLCStatus)" dark class="font-weight-bold">
                          {{ getGlcStatusTxt(Details.LavGLCStatus) }}
                        </v-chip>
                      </div>
                    </div>

                    <div class="d-flex flex-wrap gap-2 pt-2 border-top">
                      <v-btn v-if="Details.LavDownloadFlag" :href="Details.LavFilePath" target="_blank" color="error"
                        x-small class="premium-btn elevation-2 mr-2 mb-2">
                        <v-icon left x-small>mdi-file-pdf-box</v-icon> PDF
                      </v-btn>
                      <v-btn v-if="Details.LavRegenerateFlag" @click="regeneratePDF(Details.LomTrainingLavId)"
                        color="amber darken-2" dark x-small class="premium-btn elevation-2 mb-2">
                        <v-icon left x-small>mdi-sync</v-icon> Re-Generate
                      </v-btn>
                      <v-btn v-if="Details.ApprovalFlag" color="success" x-small
                        class="premium-btn elevation-2 mr-2 mb-2" @click="LavApprovalStatus(Details)">
                        <v-icon left x-small>mdi-check-decagram</v-icon> Acknowledgement
                      </v-btn>
                      <v-btn v-if="Details.LavGLCApprovalFlag" color="indigo" dark x-small
                        class="premium-btn elevation-2 mr-2 mb-2" @click="GlcAcknowledgement(Details, 2)">
                        <v-icon left x-small>mdi-shield-check</v-icon> GLC Recommendation
                      </v-btn>

                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
      </v-row>
    </v-container>
    <!-- Administrative Purpose Only Card -->
    <!-- Premium Administrative Section -->
    <v-container v-if="tableData1.length > 0 && Records.PermissionFlag" class="mt-12">
      <v-card class="admin-premium-card elevation-6">
        <div class="admin-premium-header d-flex justify-space-between align-center" dark>
          <div class="d-flex align-center">
            <v-avatar color="rgba(255,255,255,0.2)" size="48" class="mr-4">
              <v-icon color="white">mdi-shield</v-icon>
            </v-avatar>
            <div>
              <div class="text-overline white--text opacity-70" style="line-height: 1">Administrative Dashboard</div>
              <div class="text-h5 font-weight-black white--text">Official Oversight</div>
            </div>
          </div>

          <div class="d-flex align-center" v-if="Records.DocumentPath && Records.StatusText">
            <v-chip :color="Records.Status === 1 ? 'success' : 'error'" class="custom-chip px-6 mr-3 elevation-2" dark>
              <v-icon left small>{{ Records.Status === 1 ? 'mdi-check-decagram' : 'mdi-alert-circle' }}</v-icon>
              {{ Records.StatusText }}
            </v-chip>
            <v-btn v-if="Records.PdfDownloadFlag" :href="Records.DocumentPath" target="_blank" fab small
              color="white lighten-4" elevation="2">
              <v-icon color="error">mdi-file-pdf-box</v-icon>
            </v-btn>
          </div>
        </div>

        <v-card-text class="pa-8">
          <v-row>
            <v-col cols="12" md="8">
              <v-simple-table class="metric-table">
                <thead>
                  <tr>
                    <th class="text-left">Operational Metric</th>
                    <th class="text-center">Current Value</th>
                    <th class="text-right">Progress Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <div class="d-flex align-center">
                        <v-icon small color="indigo" class="mr-3">mdi-file-document-outline</v-icon>
                        <span class="font-weight-medium">First Half Submission Rate</span>
                      </div>
                    </td>
                    <td class="text-center">
                      <span class="font-weight-bold grey--text text--darken-3">{{ Records.LdmtCompletedCount }}</span>
                      <span class="grey--text text--lighten-1"> / {{ Records.LdmtLomCount }}</span>
                    </td>
                    <td class="text-right">
                      <div class="d-flex align-center justify-end">
                        <v-progress-linear :value="Records.LdmtPercentage" color="indigo" height="6" rounded
                          class="mr-3" style="width: 100px"></v-progress-linear>
                        <span class="font-weight-black indigo--text">{{ Records.LdmtPercentage }}%</span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <div class="d-flex align-center">
                        <v-icon small color="green" class="mr-3">mdi-file-document-outline</v-icon>
                        <span class="font-weight-medium">Second Half Submission Rate</span>
                      </div>
                    </td>
                    <td class="text-center">
                      <span class="font-weight-bold grey--text text--darken-3">{{ Records.LavCompletedCount }}</span>
                      <span class="grey--text text--lighten-1"> / {{ Records.LavLomCount }}</span>
                    </td>
                    <td class="text-right">
                      <div class="d-flex align-center justify-end">
                        <v-progress-linear :value="Records.LavPercentage" color="green" height="6" rounded class="mr-3"
                          style="width: 100px"></v-progress-linear>
                        <span class="font-weight-black green--text">{{ Records.LavPercentage }}%</span>
                      </div>
                    </td>
                  </tr>
                  <tr style="background: #f8f9fa">
                    <td>
                      <div class="d-flex align-center">
                        <v-icon small color="amber darken-3" class="mr-3">mdi-star-circle</v-icon>
                        <span class="font-weight-black grey--text text--darken-4">Aggregate Regional Performance</span>
                      </div>
                    </td>
                    <td class="text-center">
                      <v-chip x-small color="amber darken-3" dark class="font-weight-bold">TOTAL</v-chip>
                    </td>
                    <td class="text-right">
                      <div class="d-flex align-center justify-end">
                        <v-progress-linear :value="Records.OverallPercentage" color="amber darken-3" height="8" rounded
                          class="mr-3" style="width: 100px"></v-progress-linear>
                        <span class="font-weight-black amber--text text--darken-4">{{ Records.OverallPercentage
                          }}%</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>

            <v-col cols="12" md="4" class="d-flex align-center justify-center border-left">
              <div class="text-center">
                <v-icon size="64" color="indigo lighten-4" class="mb-4">mdi-file-check-outline</v-icon>
                <div class="text-h6 grey--text text--darken-2 mb-4">Ready for Finalization?</div>
                <v-btn color="indigo darken-4" rounded dark large class="premium-btn px-8 elevation-4"
                  @click="MspApprovalStatus(Records)">
                  <v-icon left>mdi-check-all</v-icon> Final Approval
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>

    <v-dialog v-model="previewRecordPrompt" max-width="75%">
      <ldmt-approval-status pageDescription="First Half Approval Status" category="1" :zoneCode="ZoneCode"
        :regionCode="RegionCode" :showDialog="previewRecordPrompt" @hideDialog="hidePreviewRecordPrompt($event)"
        :row="selectedData" v-if="previewRecordPrompt"></ldmt-approval-status>
    </v-dialog>
    <v-dialog v-model="lavRecordPrompt" max-width="75%">
      <lav-approval-status pageDescription="Second Half Approval Status" category="2" :zoneCode="ZoneCode"
        :regionCode="RegionCode" :showLavDialog="lavRecordPrompt" @hideLavDialog="hideLavRecordPrompt($event)"
        :rowData="recordData" v-if="lavRecordPrompt"></lav-approval-status>
    </v-dialog>
    <v-dialog v-model="mspRecordPrompt" max-width="75%" max-height="200%">
      <msp-approval pageDescription="MSP Approval" :showMspDialog="mspRecordPrompt"
        @hideDialog="hideMspRecordPrompt($event)" :row="recordData" v-if="mspRecordPrompt"></msp-approval>
    </v-dialog>

    <!-- GLC Acknowledgement Dialog -->
    <v-dialog v-model="glcRecordPrompt" max-width="75%">
      <glc-acknowledgement-status pageDescription="GLC Recommendation Status" :showDialog="glcRecordPrompt"
        @hideDialog="hideGlcRecordPrompt($event)" :row="glcData" :category="glcCategory"
        v-if="glcRecordPrompt"></glc-acknowledgement-status>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import LdmtApprovalStatus from "@/view/pages/erp/ldmt/LdmtApprovalStatus.vue";
import LavApprovalStatus from "@/view/pages/erp/ldmt/LavApprovalStatus.vue";
import MspApproval from "@/view/pages/erp/ldmt/MspApproval.vue";
import GlcAcknowledgementStatus from "@/view/pages/erp/ldmt/GlcAcknowledgementStatus.vue";
export default {
  mixins: [common],
  components: { LdmtApprovalStatus, LavApprovalStatus, MspApproval, GlcAcknowledgementStatus },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      OverlayLoadingFlag: false,
      StartupLoadingFlag: false,
      FloatingButtonFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      ResultFlag: false,
      mspRecordPrompt: false,
      glcRecordPrompt: false,
      glcData: {},
      glcCategory: 1,
      LdmtData: {},
      LavData: {},

      rows: [],
      tableColumns1: {},
      selected: [],
      search: "",
      CurrentYearId: 0,

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      selectedData: {},

      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,
      lavRecordPrompt: false,

      TableDisplayFlag1: false,

      zone: {},
      lom: {},
      Details: [],

      select: "Item 1",
      items: ["Item 1", "Item 2", "Item 3", "Item 4"],
      checkbox: false,
      Records: [],
      JciYearCode: "",

      AreaCodeRules: [(v) => !!v || "Area is required"],
      AreaCode: "",
      AreaCodeOptions: [],
      AreaCodeOptionsLoading: false,

      ZoneCodeRules: [(v) => !!v || "Zone is required"],
      ZoneCode: "",
      ZoneCodeOptions: [],
      ZoneCodeOptionsLoading: false,

      RegionCodeRules: [(v) => !!v || "Region is required"],
      RegionCode: "",
      RegionCodeOptions: [],
      RegionCodeOptionsLoading: false,

      LomCode: "",
      LomCodeOptions: [],
      LomCodeOptionsLoading: false,

      ExcelLoadingFlag: false,
      search1: "",
      selected1: "",
      tableData1: [],
      tableOptions1: {},

      tableColumns2: [],
      tableData2: [],
      tableOptions2: {},

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",

      ExcelDownloadFlag: false,
      ExcelFileName: "",
      ExcelFields: {},
    };
  },
  mounted() { },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var PagePermissionFlag = tr.PagePermissionFlag;
        console.log({ PagePermissionFlag });

        if (!PagePermissionFlag) {
          this.$router.push("/access-denied");
        }
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomCode = LomId;

        this.pageData();
      }
    },
    SearchId: function () {
      console.log("watch SearchId");
      this.rows = [];
    },
    AreaCodeOptions: function () {
      console.log("watch AreaCodeOptions");
      this.AreaCodeOptionsLoading = false;
    },
    ZoneCodeOptions: function () {
      console.log("watch ZoneCodeOptions");
      this.ZoneCodeOptionsLoading = false;
    },
    RegionCodeOptions: function () {
      console.log("watch RegionCodeOptions");
      this.RegionCodeOptionsLoading = false;
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "ldmt",
        Action: "report_1",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
      this.getAreaCodeOptions();
    },
    ApprovalStatus(tr) {
      console.log("ApprovalStatus called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.previewRecordPrompt = true;
        this.selectedData = tr;
      } else {
        var message = "Kindly select one record to preview";
        this.toast("error", message);
      }
    },
    hidePreviewRecordPrompt(flag) {
      console.log("hidePreviewRecordPrompt called");
      console.log({ flag });
      this.previewRecordPrompt = false;
      if (flag) {
        this.searchForm();
      }
    },
    LavApprovalStatus(tr) {
      console.log("LavApprovalStatus called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.lavRecordPrompt = true;
        this.recordData = tr;
      } else {
        var message = "Kindly select one record to preview";
        this.toast("error", message);
      }
    },
    MspApprovalStatus(tr) {
      console.log("MspApprovalStatus called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.mspRecordPrompt = true;
        this.recordData = tr;
      } else {
        var message = "Kindly select one record to preview";
        this.toast("error", message);
      }
    },
    GlcAcknowledgement(tr, category) {
      console.log("GlcAcknowledgement called");
      var n1 = Object.keys(tr).length;
      if (n1 > 0) {
        this.glcRecordPrompt = true;
        this.glcData = tr;
        this.glcCategory = category;
      } else {
        this.toast("error", "Kindly select a record");
      }
    },
    hideGlcRecordPrompt(flag) {
      console.log("hideGlcRecordPrompt called");
      console.log({ flag });
      this.glcRecordPrompt = false;
      if (flag) {
        this.searchForm();
      }
    },
    hideLavRecordPrompt(flag) {
      console.log("hidePreviewRecordPrompt called");
      console.log({ flag });
      this.lavRecordPrompt = false;
      if (flag) {
        this.searchForm();
      }
    },
    hideMspRecordPrompt(flag) {
      console.log("hideMspRecordPrompt called");
      console.log({ flag });
      this.mspRecordPrompt = false;
      if (flag) {
        this.searchForm();
      }
    },
    addData(tr) {
      console.log("addData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        var url = "/ldmt/send-report";
        var LomCode = tr.LomId;
        console.log({ LomCode });
        var params = {
          v1: LomCode,
        };
        this.pageRedirect(params, url);
      } else {
        var message = "Kindly select one LO to send report";
        this.sweetAlert("error", message, false);
      }
    },
    resetForm() {
      this.search = "";
      this.tableData2 = [];
      this.tableData1 = [];
      this.selected = [];
      this.$refs.form2.reset();
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    hidePreviewRecordPrompt(flag) {
      console.log("hidePreviewRecordPrompt called");
      console.log({ flag });
      this.previewRecordPrompt = false;
      if (flag) {
        this.searchForm();
      }
    },
    getAreaCodeOptions() {
      console.log("getAreaCodeOptions called");
      this.AreaCodeOptionsLoading = true;
      var selectbox1_source = "AreaCode";
      var selectbox1_destination = "AreaCodeOptions";
      var selectbox1_url = "api/year-wise-lom/area-options";
      var selectbox1_conditions_array = {
        UserInterface: 4,
        YearCode: this.JciYearCode,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getZoneCodeOptions() {
      console.log("getZoneCodeOptions called");
      var yearCode = this.JciYearCode;
      var areaCode = this.AreaCode;
      this.LomCodeOptions = [];
      console.log("yearCode=" + yearCode + ", areaCode=" + areaCode);
      if (yearCode != "" && areaCode != "") {
        this.ZoneCode = "";
        this.ZoneCodeOptionsLoading = true;
        this.tableData2 = [];
        var selectbox1_source = "ZoneCode";
        var selectbox1_destination = "ZoneCodeOptions";
        var selectbox1_url = "api/year-wise-zone-options";
        var selectbox1_conditions_array = {
          UserInterface: 7,
          AreaCode: areaCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getRegionCodeOptions() {
      console.log("getRegionCodeOptions called");
      var YearCode = this.JciYearCode;
      var AreaCode = this.AreaCode;
      var ZoneCode = this.ZoneCode;
      this.RegionCodeOptions = [];
      this.LomCodeOptions = [];
      console.log(
        "YearCode=" +
        YearCode +
        ", AreaCode=" +
        AreaCode +
        ", ZoneCode=" +
        ZoneCode
      );
      if (YearCode != "" && AreaCode != "" && ZoneCode != "") {
        this.RegionCode = "";
        this.RegionCodeOptionsLoading = true;
        this.tableData2 = [];
        var selectbox1_source = "RegionCode";
        var selectbox1_destination = "RegionCodeOptions";
        var selectbox1_url = "api/year-wise-lom/region-options";
        var selectbox1_conditions_array = {
          UserInterface: 4,
          YearCode: YearCode,
          AreaCode: AreaCode,
          ZoneCode: ZoneCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    searchForm() {
      console.log("searchForm called");

      this.mspCertificate();

      var LomCode = this.$session.get("LomId");
      LomCode = LomCode == (null || undefined) ? 0 : LomCode;
      console.log("LomCode=" + LomCode);

      var validate1 = this.$refs.form2.validate();
      console.log("validate1=" + validate1);

      if (validate1) {
        this.TrainingData = {};

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url =
          server_url + "api/lom-training/ldmt-lav-consolidate-report";
        var upload = {
          UserInterface: 1,
          Area: this.AreaCode,
          Zone: this.ZoneCode,
          Region: this.RegionCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
          server_url +
          ", token=" +
          token +
          ", add_url=" +
          add_url
        );

        this.tableData1 = [];
        this.tableColumns1 = [];
        this.LoadingFlag = true;
        this.SearchFlag = true;
        this.LdmtData = {};
        this.LavData = {};

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;
        let tableOptions1 = {};
        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.LoadingFlag = false;
            thisIns.SearchFlag = false;
            thisIns.ResultFlag = true;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            thisIns.ExcelDownloadFlag = false;

            if (flag == 1) {
              thisIns.tableColumns1 = records.ZoneVicePresident;
              thisIns.tableData1 = records.LomData;
              console.log("tableData1", thisIns.tableData1);
              console.log("tableColumns1", thisIns.tableColumns1);
              thisIns.LdmtData = records.LdmtData;
              thisIns.LavData = records.LavData;
              thisIns.tableOptions1 = response.data.records.TableOptions;
              tableOptions1 = thisIns.tableOptions1;
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.toast("error", error);
            thisIns.LoadingFlag = false;
            thisIns.SearchFlag = false;
          });
      } else {
        var message = "Kindly fill required fields.";
        this.toast("error", message);
      }
    },
    generateExcelReport() {
      console.log("generateExcelReport is called");

      var validate1 = this.$refs.form2.validate();
      var AreaCode = this.AreaCode;
      console.log("validate1=" + validate1 + ", AreaCode=" + AreaCode);
      this.ExcelLoadingFlag = true;

      if (validate1) {
        this.ExcelFileName = "";

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/lom-training/generate-excel-report";
        var upload = {
          UserInterface: 1,
          AreaCode: this.AreaCode,
          ZoneCode: this.ZoneCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
          server_url +
          ", token=" +
          token +
          ", add_url=" +
          add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()

            thisIns.ExcelLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.ExcelFileName = records.ExcelFileName;
              thisIns.ExcelDownloadFlag = records.ExcelDownloadFlag;
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SearchFlag = false;
            thisIns.ExcelLoadingFlag = false;
          });
      } else {
        var message = "Kindly fill the required fields";
        this.toast("error", message);
      }
    },
    regeneratePDF(LomTrainingId) {
      console.log("regeneratePDF called");

      console.log("LomTrainingId=", LomTrainingId);

      var token = this.$session.get("token") || "";

      if (!LomTrainingId) {
        this.toast("error", "Training ID is missing.");
        return;
      }
      this.OverlayLoadingFlag = true;
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/lom-training/regenerate-pdf";
      var upload = {
        UserInterface: 1,
        LomTraining: LomTrainingId,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.OverlayLoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          console.log("output=" + output + ", flag=" + flag);

          if (flag == 1) {
            thisIns.toast("success", output);
            thisIns.searchForm();
          } else {
            thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.toast("error", error);
          thisIns.OverlayLoadingFlag = false;
        });
    },
    mspCertificate() {
      console.log("mspCertificate called");

      console.log("AreaCode=", this.AreaCode);
      console.log("ZoneCode=", this.ZoneCode);
      console.log("RegionCode=", this.RegionCode);

      var token = this.$session.get("token") || "";

      this.OverlayLoadingFlag = true;
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/msp-certificate/info";
      var upload = {
        UserInterface: 1,
        Area: this.AreaCode,
        Zone: this.ZoneCode,
        Region: this.RegionCode,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var flag = 0;
      var records = "";

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.OverlayLoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;

          records = response.data.records;
          console.log({ records });

          console.log("output=" + output + ", flag=" + flag);

          if (flag == 1) {
            thisIns.Records = records;
            console.log("Records", thisIns.Records);
            thisIns.toast("success", output);
          } else {
            thisIns.toast("error", output);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.toast("error", error);
          thisIns.OverlayLoadingFlag = false;
        });
    },
    getGlcStatusTxt(status) {
      if (status == 1) return "Yes";
      if (status == 2) return "No";
      return "Pending";
    },
    getGlcStatusColor(status) {
      if (status == 1) return "#3A67B1"; // Navy
      if (status == 2) return "#ff7f50"; // Coral/Orange
      return "#9e9e9e"; // Grey
    },
  },
  beforeMount() {
    this.refreshPageData();
    this.tableColumns1.push(
      { text: "Paid Date", value: "PaidDateTxt" },
      { text: "Year", value: "YearName" },
      {
        text: "Subscription Description",
        value: "ContributionTypeDescription",
      },
      { text: "Paid Count", value: "PaidCount" },
      { text: "Remaining Count", value: "RemainingCount" },
      { text: "Add members", value: "actions" }
    );
  },
};
</script>
<style lang="scss">
/* This is for documentation purposes and will not be needed in your application */

@import "~vue-tabulator/dist/scss/bootstrap/tabulator_bootstrap4";

#ldmt-consolidate-report {
  /* Approved Design System */
  --navy: #3A67B1;
  --aqua: #0097D7;
  --gold: #EDBE38;
  --seaform: #56BDA3;
  --text-90: #414042;
  --text-off-black: #0A0F29;
  --white: #FFFFFF;

  font-size: 1.15rem !important;

  .card-title {
    font-size: 28px;
    font-family: "Lora", serif;
    padding: 24px;
    text-align: center;
    word-wrap: break-word;
    color: var(--text-off-black);
  }

  /* Typography Scaling */
  h1 {
    font-size: 3.5rem !important;
  }

  h2 {
    font-size: 2.2rem !important;
  }

  .text-h3 {
    font-size: 3rem !important;
  }

  .text-h4 {
    font-size: 2.2rem !important;
  }

  .text-h5 {
    font-size: 1.9rem !important;
  }

  .text-h6 {
    font-size: 1.5rem !important;
  }

  .text-body-1 {
    font-size: 1.25rem !important;
  }

  .text-body-2 {
    font-size: 1.15rem !important;
  }

  .text-overline {
    font-size: 0.9rem !important;
    font-weight: 700 !important;
  }

  .text-caption {
    font-size: 1rem !important;
  }

  .premium-header-card {
    background: linear-gradient(135deg, var(--navy) 0%, #1a237e 100%) !important;
    border-radius: 0 0 40px 40px !important;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2) !important;
    position: relative;
    overflow: hidden !important;
  }

  /* Bubble Animation Styles */
  .header-bubbles {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    z-index: 1;
  }

  .bubble {
    position: absolute;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    backdrop-filter: blur(2px);
    animation: float-bubble 15s infinite ease-in-out;
  }

  .b-1 {
    width: 120px;
    height: 120px;
    top: 10%;
    left: 5%;
    animation-delay: 0s;
  }

  .b-2 {
    width: 180px;
    height: 180px;
    top: 40%;
    left: 80%;
    animation-delay: -2s;
  }

  .b-3 {
    width: 80px;
    height: 80px;
    top: 70%;
    left: 20%;
    animation-delay: -5s;
  }

  .b-4 {
    width: 150px;
    height: 150px;
    top: -10%;
    left: 40%;
    animation-delay: -8s;
  }

  .b-5 {
    width: 60px;
    height: 60px;
    top: 50%;
    left: 60%;
    animation-delay: -3s;
  }

  @keyframes float-bubble {

    0%,
    100% {
      transform: translateY(0) translateX(0) scale(1);
    }

    33% {
      transform: translateY(-20px) translateX(10px) scale(1.1);
    }

    66% {
      transform: translateY(10px) translateX(-15px) scale(0.9);
    }
  }

  .premium-header-card::before {
    content: "";
    position: absolute;
    top: -50%;
    right: -20%;
    width: 400px;
    height: 400px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 50%;
  }

  .gold--text {
    color: var(--gold) !important;
  }

  .search-card {
    margin-top: -30px !important;
    border-radius: 24px !important;
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1) !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
    background: rgba(255, 255, 255, 0.98) !important;
    backdrop-filter: blur(10px);
  }

  .stat-card {
    border-radius: 20px !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    border: none !important;
    position: relative;
    overflow: hidden;
  }

  .stat-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
  }

  .zvp-premium-card {
    background: linear-gradient(135deg, var(--navy) 0%, var(--aqua) 100%) !important;
  }

  .ldmt-premium-card {
    background: linear-gradient(135deg, var(--seaform) 0%, #2e7d32 100%) !important;
  }

  .lav-premium-card {
    background: linear-gradient(135deg, var(--aqua) 0%, #0277bd 100%) !important;
  }

  .lom-details-card {
    background: white !important;
    border-radius: 24px !important;
    border-left: 8px solid var(--navy) !important;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08) !important;
    color: var(--text-off-black) !important;
  }

  .training-category-card {
    border-radius: 20px !important;
    transition: transform 0.3s ease;
    height: 100%;
  }

  .ldmt-box {
    border-top: 5px solid var(--seaform);
    background: #f1f8e9 !important;
    color: var(--text-90) !important;
  }

  .lav-box {
    border-top: 5px solid var(--aqua);
    background: #e1f5fe !important;
    color: var(--text-90) !important;
  }

  .premium-btn {
    text-transform: none !important;
    letter-spacing: 0.8px !important;
    font-weight: 700 !important;
    border-radius: 14px !important;
    transition: all 0.3s ease !important;
    font-size: 1.1rem !important;
    padding: 12px 24px !important;
  }

  .btn-search {
    background: var(--gold) !important;
    color: var(--text-off-black) !important;
    box-shadow: 0 6px 18px rgba(237, 190, 56, 0.4) !important;
  }

  .btn-reset {
    background: var(--text-90) !important;
    color: var(--white) !important;
  }

  .admin-premium-card {
    background: white !important;
    border-radius: 28px !important;
    border: 1px solid #e0e0e0 !important;
    overflow: hidden;
  }

  .admin-premium-header {
    background: linear-gradient(135deg, var(--navy) 0%, var(--aqua) 100%) !important;
    padding: 28px !important;
  }

  .metric-table th {
    font-weight: 800 !important;
    color: var(--navy) !important;
    text-transform: uppercase;
    font-size: 1rem !important;
    letter-spacing: 1.2px;
    padding: 20px !important;
  }

  .metric-table td {
    padding: 24px 20px !important;
    font-size: 1.2rem !important;
    color: var(--text-90) !important;
  }

  .excel-action-box {
    border: 1px solid #e0e0e0 !important;
    background: linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%) !important;
  }

  .btn-excel-generate {
    background: linear-gradient(135deg, #ff8f00 0%, #ef6c00 100%) !important;
    box-shadow: 0 6px 20px rgba(239, 108, 0, 0.3) !important;
  }

  .btn-excel-generate:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(239, 108, 0, 0.4) !important;
  }

  .btn-excel-download {
    background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%) !important;
    box-shadow: 0 6px 20px rgba(27, 94, 32, 0.3) !important;
  }

  .btn-excel-download:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(27, 94, 32, 0.4) !important;
  }

  .custom-chip {
    font-weight: 700 !important;
    border-radius: 10px !important;
    font-size: 1rem !important;
    padding: 0 16px !important;
  }

  .v-autocomplete.v-text-field--outlined fieldset {
    border-color: #cfd8dc;
  }

  .v-autocomplete.v-text-field--outlined.v-input--is-focused fieldset {
    border-color: var(--navy);
    border-width: 2px;
  }

  .border-right-md {
    border-right: 1px solid #eeeeee;
  }
}
</style>